import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import axios from 'axios'
import { useFormik } from 'formik'
import FilmProductionAnalyzerResults from '../components/filmAnalyzer/FilmProductionAnalyzerResults'
import FieldError from '../components/productionTools/FieldError'
import AiToolPaymentErrorBanner from '../components/productionTools/AiToolPaymentErrorBanner'
import ProductionToolsHeader from '../components/productionTools/ProductionToolsHeader'
import ProductionToolsSelect from '../components/productionTools/ProductionToolsSelect'
import AiToolSamplePdfButton from '../components/productionTools/AiToolSamplePdfButton'
import AiToolRunButton from '../components/productionTools/AiToolRunButton'
import AiToolPriceBadge from '../components/productionTools/AiToolPriceBadge'
import '../css/FilmProductionAnalyzer.css'
import {
  CURRENCY_HINTS,
  CURRENCY_SYMBOLS,
  LOC_TYPE_LABELS,
  PM_LABELS,
  STREAM_MESSAGES,
} from '../utils/filmAnalyzer/constants'
import { useAiToolPayment } from '../hooks/useAiToolPayment'
import { useAiToolPaymentError } from '../hooks/useAiToolPaymentError'
import {
  AI_TOOL_TYPES,
  buildProductionAnalyzerFormData,
} from '../utils/aiTool/payment'
import {
  AI_TOOL_LIMITS,
  getFormBannerError,
  productionAnalyzerSchema,
} from '../utils/aiTool/validationSchemas'
import { getAiToolApiConfig } from '../utils/aiTool/apiConfig'
import {
  handleDigitsOnlyChange,
  preventInvalidNumberKeyDown,
  preventNumberInputWheelChange,
} from '../utils/aiTool/numberInputHandlers'

const INITIAL_FORM = {
  projectTitle: '',
  email: '',
  uname: '',
  projectType: '',
  currency: '',
  country: '',
  locations: '',
  shootDays: '',
  budget: '',
  actors: '',
  productionMode: 'controlled',
  crowd: 'no',
  night: 'low',
  scenes: '',
  locationType: 'mixed',
  crew: 'medium',
  post: 'standard',
  musicBudget: '',
  postBudget: '',
}

const PillGroup = ({ name, value, onChange, options }) => (
  <div className="pill-group">
    {options.map((opt) => (
      <React.Fragment key={opt.value}>
        <input
          type="radio"
          name={name}
          id={`${name}-${opt.value}`}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
        />
        <label htmlFor={`${name}-${opt.value}`}>{opt.label}</label>
      </React.Fragment>
    ))}
  </div>
)

const FilmProductionAnalyzer = () => {
  const [mode, setMode] = useState('quick')
  const [error, setError] = useState('')
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [streamMsg, setStreamMsg] = useState(STREAM_MESSAGES[0])
  const [results, setResults] = useState(null)
  const [resultInputs, setResultInputs] = useState(null)
  const resultsAnchorRef = useRef(null)
  const msgTimerRef = useRef(null)
  const [currencyList, setCurrencyList] = useState([])
  const { initiatePayment } = useAiToolPayment()

  const isAdv = mode === 'advanced'

  const clearStreamTimer = useCallback(() => {
    if (msgTimerRef.current) {
      clearInterval(msgTimerRef.current)
      msgTimerRef.current = null
    }
  }, [])

  const startStreamTimer = useCallback(() => {
    clearStreamTimer()
    setStreamMsg(STREAM_MESSAGES[0])
    let msgIdx = 0
    msgTimerRef.current = setInterval(() => {
      msgIdx = Math.min(STREAM_MESSAGES.length - 1, msgIdx + 1)
      setStreamMsg(STREAM_MESSAGES[msgIdx])
    }, 2000)
  }, [clearStreamTimer])

  const formik = useFormik({
    initialValues: { ...INITIAL_FORM },
    validationSchema: productionAnalyzerSchema,
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: async (values) => {
      clearPaymentError()
      setError('')

      const locs = parseInt(values.locations, 10) || 0
      const sd = parseInt(values.shootDays, 10) || 0
      const budget = parseInt(values.budget, 10) || 0
      const actors = parseInt(values.actors, 10) || 0

      setPaymentLoading(true)
      setAnalyzing(false)
      setResults(null)
      clearStreamTimer()

      const title = values.projectTitle.trim() || 'Untitled Production'
      const currSym =
        selectedCurrency?.symbol || CURRENCY_SYMBOLS[values.currency] || ''
      const scenes = isAdv ? parseInt(values.scenes, 10) || 0 : 0
      const musicB = isAdv ? parseInt(values.musicBudget, 10) || 0 : 0
      const postB = isAdv ? parseInt(values.postBudget, 10) || 0 : 0

      const inputs = {
        title,
        email: values.email.trim(),
        pt: values.projectType,
        pm: values.productionMode,
        pmLabel: PM_LABELS[values.productionMode],
        locs,
        sd,
        budget,
        actors,
        crowd: values.crowd,
        night: values.night,
        isAdv,
        scenes,
        locType: values.locationType,
        locTypeLabel: LOC_TYPE_LABELS[values.locationType],
        crew: values.crew,
        post: values.post,
        musicB,
        postB,
        currency: values.currency,
        currSym,
        country: values.country.trim() || 'unspecified region',
      }

      try {
        const formData = buildProductionAnalyzerFormData(values, mode)

        const paymentResult = await initiatePayment({
          toolType: AI_TOOL_TYPES.PRODUCTION_ANALYZER,
          email: values.email.trim(),
          fullName: values.uname.trim(),
          formData,
          rawFormValues: values,
          rawMode: mode,
          onPaymentVerified: () => {
            setPaymentLoading(false)
            setAnalyzing(true)
            startStreamTimer()
          },
          onDismiss: () => {
            setPaymentLoading(false)
          },
        })

        if (paymentResult?.redirecting) {
          return
        }

        const { aiResult } = paymentResult

        const output = aiResult?.data?.output
        if (!output) {
          throw new Error(
            aiResult?.message || 'Analysis failed. No results returned.',
          )
        }

        setResults(output)
        setResultInputs({
          ...inputs,
          aiuId: aiResult.data?.aiuId,
          pdfContent: aiResult.data?.pdfContent,
        })
      } catch (err) {
        if (err.message === 'Payment cancelled') {
          return
        }
        setError(
          err?.response?.data?.message ||
            err.message ||
            'Something went wrong. Please try again.',
        )
      } finally {
        clearStreamTimer()
        setAnalyzing(false)
        setPaymentLoading(false)
      }
    },
  })

  const { setValues: setFormValues } = formik

  const handleRestoreForm = useCallback(
    (restored) => {
      setFormValues(restored.formValues)
      setMode(restored.mode)
    },
    [setFormValues],
  )

  const { paymentError, clearPaymentError } = useAiToolPaymentError(
    AI_TOOL_TYPES.PRODUCTION_ANALYZER,
    handleRestoreForm,
  )

  const selectedCurrency = useMemo(
    () => currencyList?.find((item) => item.title === formik.values.currency),
    [currencyList, formik.values.currency],
  )

  const currencyHint = selectedCurrency
    ? `(${selectedCurrency.symbol})`
    : CURRENCY_HINTS[formik.values.currency] || ''

  const displayError = getFormBannerError(formik, error)
  const isSubmitting = paymentLoading || analyzing

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/dropdown/list`,
        getAiToolApiConfig(),
      )
      .then((response) => {
        if (response?.data?.data?.currency) {
          setCurrencyList(response?.data?.data?.currency || [])
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }, [])

  const { setFieldValue, values: formValues } = formik

  useEffect(() => {
    if (currencyList?.length === 0) return

    if (
      formValues.currency &&
      currencyList?.some((item) => item.title === formValues.currency)
    ) {
      return
    }

    const defaultCurrency =
      currencyList?.find(
        (item) =>
          item.title?.toUpperCase().includes('INR') || item.symbol === '₹',
      ) || currencyList?.[0]

    setFieldValue('currency', defaultCurrency?.title || '')
  }, [currencyList, formValues.currency, setFieldValue])

  useEffect(() => {
    return () => {
      if (msgTimerRef.current) clearInterval(msgTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (results && resultsAnchorRef.current) {
      resultsAnchorRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [results])

  const resetForm = () => {
    setResults(null)
    setResultInputs(null)
    setError('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="production-tools-theme film-analyzer-page">
      <Helmet>
        <title>PitchBlu Production Intelligence Engine</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Anton&family=Bricolage+Grotesque:opsz,wght@12..96,200..800&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <ProductionToolsHeader badge="Production Intelligence" />

      {/* <div className="film-strip" /> */}

      <div className="container">
        <header>
          <div className="header-eyebrow">
            <span
              style={{
                display: 'inline-block',
                width: 28,
                height: 1,
                background: 'var(--gold)',
                opacity: 0.4,
                verticalAlign: 'middle',
              }}
            />
            &nbsp;PitchBlu Production Intelligence&nbsp;
            <span
              style={{
                display: 'inline-block',
                width: 28,
                height: 1,
                background: 'var(--gold)',
                opacity: 0.4,
                verticalAlign: 'middle',
              }}
            />
          </div>
          <h1>
            Film Production
            <br />
            Analyzer
          </h1>
          <p className="subtitle">
            AI-powered production analysis. Our engine reasons about your
            project the way an experienced line producer would — holistically,
            honestly, precisely.
          </p>
          <div className="ai-tool-hero-actions">
            <AiToolPriceBadge
              toolType={AI_TOOL_TYPES.PRODUCTION_ANALYZER}
              mode={mode}
              variant="hero"
            />
            <AiToolSamplePdfButton
              toolType={AI_TOOL_TYPES.PRODUCTION_ANALYZER}
            />
          </div>
        </header>

        <div className="mode-toggle">
          <button
            type="button"
            className={`mode-btn ${mode === 'quick' ? 'active' : ''}`}
            onClick={() => setMode('quick')}
          >
            Short Analysis
          </button>
          <button
            type="button"
            className={`mode-btn ${mode === 'advanced' ? 'active' : ''}`}
            onClick={() => setMode('advanced')}
          >
            Advanced Estimation <span className="mode-badge">PRO</span>
          </button>
        </div>

        <div className={`form-card ${isAdv ? 'advanced-mode' : ''}`}>
          <form noValidate onSubmit={formik.handleSubmit}>
            <AiToolPaymentErrorBanner
              message={paymentError}
              onDismiss={clearPaymentError}
            />
            <div className="form-section">
              <div className="section-title">Project Overview</div>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="projectTitle">Project Title</label>
                  <input
                    id="projectTitle"
                    type="text"
                    placeholder="e.g. The Last Rain"
                    value={formik.values.projectTitle}
                    onChange={formik.handleChange}
                    name="projectTitle"
                    maxLength={AI_TOOL_LIMITS.PROJECT_TITLE_MAX}
                    autoComplete="off"
                  />
                  <FieldError formik={formik} name="projectTitle" />
                </div>
                <div className="field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="example@gmail.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    name="email"
                    maxLength={AI_TOOL_LIMITS.EMAIL_MAX}
                  />
                  <FieldError formik={formik} name="email" />
                </div>
                <div className="field full">
                  <label htmlFor="uname">Full Name</label>
                  <input
                    id="uname"
                    type="text"
                    placeholder="e.g. Ravi Kumar"
                    value={formik.values.uname}
                    onChange={formik.handleChange}
                    name="uname"
                    maxLength={AI_TOOL_LIMITS.NAME_MAX}
                    autoComplete="name"
                  />
                  <FieldError formik={formik} name="uname" />
                </div>
                <div className="field full">
                  <label>Project Type</label>
                  <ProductionToolsSelect
                    autoComplete="new-password"
                    value={formik.values.projectType}
                    onChange={formik.handleChange}
                    name="projectType"
                  >
                    <option value="" disabled>
                      Select scale...
                    </option>
                    <option value="indie">Indie</option>
                    <option value="low">Low-budget</option>
                    <option value="mid">Mid-scale</option>
                    <option value="large">Large</option>
                  </ProductionToolsSelect>
                  <FieldError formik={formik} name="projectType" />
                </div>
                <div className="field">
                  <label>Number of Locations</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 5"
                    value={formik.values.locations}
                    onChange={handleDigitsOnlyChange(formik.handleChange)}
                    onKeyDown={preventInvalidNumberKeyDown}
                    onWheel={preventNumberInputWheelChange}
                    name="locations"
                  />
                  <FieldError formik={formik} name="locations" />
                </div>
                <div className="field">
                  <label>Shoot Days</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 25"
                    value={formik.values.shootDays}
                    onChange={handleDigitsOnlyChange(formik.handleChange)}
                    onKeyDown={preventInvalidNumberKeyDown}
                    onWheel={preventNumberInputWheelChange}
                    name="shootDays"
                  />
                  <FieldError formik={formik} name="shootDays" />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-title">Budget &amp; Cast</div>
              <div className="form-grid">
                <div className="field">
                  <label>Currency</label>
                  <ProductionToolsSelect
                    autoComplete="new-password"
                    value={formik.values.currency}
                    onChange={formik.handleChange}
                    name="currency"
                  >
                    {currencyList?.length === 0 && (
                      <option value="">Loading currencies...</option>
                    )}
                    {currencyList?.map((item) => (
                      <option key={item.id} value={item.title}>
                        {item.symbol} — {item.title}
                      </option>
                    ))}
                  </ProductionToolsSelect>
                </div>
                <div className="field">
                  <label>Country / Region</label>
                  <input
                    type="text"
                    placeholder="e.g. India, Nigeria, UK..."
                    value={formik.values.country}
                    onChange={formik.handleChange}
                    name="country"
                    maxLength={AI_TOOL_LIMITS.COUNTRY_MAX}
                  />
                  <FieldError formik={formik} name="country" />
                </div>
                <div className="field">
                  <label>
                    Total Budget{' '}
                    <span
                      style={{
                        color: 'var(--gold)',
                        fontWeight: 400,
                        textTransform: 'none',
                        letterSpacing: 0,
                      }}
                    >
                      {currencyHint}
                    </span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Enter total budget amount"
                    value={formik.values.budget}
                    onChange={handleDigitsOnlyChange(formik.handleChange)}
                    onKeyDown={preventInvalidNumberKeyDown}
                    onWheel={preventNumberInputWheelChange}
                    name="budget"
                  />
                  <FieldError formik={formik} name="budget" />
                </div>
                <div className="field">
                  <label>Number of Actors</label>
                  <input
                    type="number"
                    min="1"
                    placeholder="e.g. 8"
                    value={formik.values.actors}
                    onChange={handleDigitsOnlyChange(formik.handleChange)}
                    onKeyDown={preventInvalidNumberKeyDown}
                    onWheel={preventNumberInputWheelChange}
                    name="actors"
                  />
                  <FieldError formik={formik} name="actors" />
                </div>
              </div>
            </div>

            <div className="form-section">
              <div className="section-title">Production Complexity</div>
              <div className="form-grid">
                <div className="field full">
                  <label>Production Mode</label>
                  <ProductionToolsSelect
                    autoComplete="new-password"
                    value={formik.values.productionMode}
                    onChange={formik.handleChange}
                    name="productionMode"
                  >
                    <option value="controlled">Controlled Production</option>
                    <option value="guerrilla">Flexible / Guerrilla</option>
                    <option value="documentary">Documentary / Real-time</option>
                    <option value="zerobud">
                      Zero Budget / Resource-driven
                    </option>
                  </ProductionToolsSelect>
                </div>
                <div className="field full">
                  <label>Crowd Scenes</label>
                  <PillGroup
                    name="crowd"
                    value={formik.values.crowd}
                    onChange={(v) => formik.setFieldValue('crowd', v)}
                    options={[
                      { value: 'no', label: 'No Crowds' },
                      { value: 'yes', label: 'Yes, Crowds' },
                    ]}
                  />
                </div>
                <div className="field full">
                  <label>Night Shoots</label>
                  <PillGroup
                    name="night"
                    value={formik.values.night}
                    onChange={(v) => formik.setFieldValue('night', v)}
                    options={[
                      { value: 'low', label: 'Low' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'high', label: 'High' },
                    ]}
                  />
                </div>
              </div>
            </div>

            <div className={`advanced-fields ${isAdv ? 'visible' : ''}`}>
              <div className="adv-divider">⸰ Advanced Parameters</div>

              <div className="form-section">
                <div className="section-title adv">
                  Scene &amp; Location Detail
                </div>
                <div className="form-grid">
                  <div className="field">
                    <label>Number of Scenes</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g. 45"
                      value={formik.values.scenes}
                      onChange={handleDigitsOnlyChange(formik.handleChange)}
                      onKeyDown={preventInvalidNumberKeyDown}
                      onWheel={preventNumberInputWheelChange}
                      name="scenes"
                    />
                    <FieldError formik={formik} name="scenes" />
                  </div>
                  <div className="field">
                    <label>Location Type</label>
                    <ProductionToolsSelect
                      autoComplete="new-password"
                      value={formik.values.locationType}
                      onChange={formik.handleChange}
                      name="locationType"
                    >
                      <option value="controlled">
                        Controlled (Indoor / Set)
                      </option>
                      <option value="mixed">Mixed</option>
                      <option value="uncontrolled">
                        Uncontrolled (Outdoor / Real)
                      </option>
                    </ProductionToolsSelect>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-title adv">
                  Crew &amp; Post-Production
                </div>
                <div className="form-grid">
                  <div className="field">
                    <label>Crew Size</label>
                    <PillGroup
                      name="crew"
                      value={formik.values.crew}
                      onChange={(v) => formik.setFieldValue('crew', v)}
                      options={[
                        { value: 'small', label: 'Small' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'large', label: 'Large' },
                      ]}
                    />
                  </div>
                  <div className="field">
                    <label>Post-Production Level</label>
                    <PillGroup
                      name="post"
                      value={formik.values.post}
                      onChange={(v) => formik.setFieldValue('post', v)}
                      options={[
                        { value: 'basic', label: 'Basic' },
                        { value: 'standard', label: 'Standard' },
                        { value: 'high', label: 'High' },
                      ]}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="section-title adv">
                  Optional Budget Breakdown
                </div>
                <div className="form-grid">
                  <div className="field">
                    <label>
                      Music Budget <span className="opt">optional</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Music / score allocation"
                      value={formik.values.musicBudget}
                      onChange={handleDigitsOnlyChange(formik.handleChange)}
                      onKeyDown={preventInvalidNumberKeyDown}
                      onWheel={preventNumberInputWheelChange}
                      name="musicBudget"
                    />
                    <FieldError formik={formik} name="musicBudget" />
                  </div>
                  <div className="field">
                    <label>
                      Post-Production Budget{' '}
                      <span className="opt">optional</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Edit / grade / sound allocation"
                      value={formik.values.postBudget}
                      onChange={handleDigitsOnlyChange(formik.handleChange)}
                      onKeyDown={preventInvalidNumberKeyDown}
                      onWheel={preventNumberInputWheelChange}
                      name="postBudget"
                    />
                    <FieldError formik={formik} name="postBudget" />
                  </div>
                </div>
              </div>
            </div>

            {displayError && (
              <div className="err-msg" style={{ display: 'block' }}>
                {displayError}
              </div>
            )}

            <AiToolRunButton
              type="submit"
              loading={isSubmitting}
            >
              {isAdv ? 'Run Advanced AI Analysis' : 'Run Short Analysis'}
            </AiToolRunButton>
          </form>
        </div>

        {analyzing && (
          <div className="stream-wrap">
            <div className="stream-card">
              <div className="stream-label">
                ● AI is analyzing your production
              </div>
              <div className="stream-dots">
                <div className="dot" />
                <div className="dot" />
                <div className="dot" />
              </div>
              <div className="stream-msg">{streamMsg}</div>
            </div>
          </div>
        )}

        <div ref={resultsAnchorRef}>
          {results && resultInputs && (
            <FilmProductionAnalyzerResults
              data={results}
              inputs={resultInputs}
              onReset={resetForm}
            />
          )}
        </div>

        <footer>
          <span>PitchBlu Production Intelligence Engine v6.0</span>
          <span className="gold">
            Production Intelligence Engine · No data stored · Global
          </span>
        </footer>
      </div>
    </div>
  )
}

export default FilmProductionAnalyzer
